/**
 * Sistema de toasts mínimo. Auto-dismiss con timer.
 *
 * Uso:
 *   const toast = useToast();
 *   toast.success("Pago registrado");
 *   toast.error("No se pudo guardar");
 *
 * Renderizar <ToastContainer /> una vez en el layout.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { CheckCircle2, AlertTriangle, Info, X } from "lucide-react";

type ToastTone = "success" | "error" | "info";

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  success: (msg: string) => void;
  error: (msg: string) => void;
  info: (msg: string) => void;
}

interface InternalContext {
  api: ToastApi;
  toasts: ToastItem[];
  dismiss: (id: number) => void;
}

const ToastContext = createContext<InternalContext | null>(null);

const DEFAULT_TIMEOUT_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? Math.floor(Math.random() * 1_000_000_000)
          : Date.now();
      setToasts((prev) => [...prev, { id, tone, message }]);
      window.setTimeout(() => dismiss(id), DEFAULT_TIMEOUT_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (msg) => push("success", msg),
      error: (msg) => push("error", msg),
      info: (msg) => push("info", msg),
    }),
    [push],
  );

  const value = useMemo(() => ({ api, toasts, dismiss }), [api, toasts, dismiss]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast debe usarse dentro de <ToastProvider>");
  }
  return ctx.api;
}

export function ToastContainer(): JSX.Element | null {
  const ctx = useContext(ToastContext);
  if (!ctx) return null;
  const { toasts, dismiss } = ctx;

  // El contenedor vive siempre (aunque esté vacío): una región aria-live
  // sólo anuncia lo que se agrega DESPUÉS de montarse. Los errores llevan
  // role="alert" para anunciarse de inmediato.
  return (
    <div
      aria-live="polite"
      aria-relevant="additions"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
    >
      {toasts.map((t) => (
        <ToastEntry key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastEntry({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: () => void;
}): JSX.Element {
  // Animación simple de entrada con CSS al montar
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setMounted(true), 10);
    return () => window.clearTimeout(id);
  }, []);

  const styles: Record<ToastTone, { box: string; icon: JSX.Element }> = {
    success: {
      box: "bg-emerald-50 border-emerald-200 text-emerald-900",
      icon: <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />,
    },
    error: {
      box: "bg-rose-50 border-rose-200 text-rose-900",
      icon: <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-700" aria-hidden="true" />,
    },
    info: {
      // VIS-13: info sin `sky` (fuera de la paleta del ecosistema).
      box: "bg-neutral-50 border-neutral-200 text-neutral-800",
      icon: <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" aria-hidden="true" />,
    },
  };
  const s = styles[toast.tone];

  return (
    <div
      role={toast.tone === "error" ? "alert" : "status"}
      className={`pointer-events-auto flex items-start gap-2 rounded-md border px-3 py-2 shadow-md text-sm transition-all duration-200 ${s.box} ${
        mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      }`}
      style={{ minWidth: 240, maxWidth: 360 }}
    >
      {s.icon}
      <span className="flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="rounded text-neutral-600 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        aria-label="Cerrar aviso"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
